<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Island vacation</h1>
      <app-cells>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by group name"
          name="search"
        />
        <template v-if="is_sales_general_manager">
          <app-date-picker @change="onChangeFilter" v-model="filter_from" placeholder="Tour start" class="datepicker--filter" />
          <app-date-picker @change="onChangeFilter" v-model="filter_to" placeholder="Tour end" class="datepicker--filter" />
          <app-select
            @input="onChangeCategory"
            v-model="category_filter"
            :options="category_options"
            :reduce="item => item.id"
            :filterable="false"
            :searchable="false"
            placeholder="Category"
            class="select--200"
          />
        </template>
      </app-cells>
      <div v-for="trips_table_item of trips_table" :key="trips_table_item.name" class="table-block">
        <app-cells v-if="trips_table_item.name">
          <h2 class="title">{{ trips_table_item.name }}</h2>
        </app-cells>
        <v-client-table
          :data="trips_table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default table-default--climbing-groups-list"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__name>
            <div class="table-default__left">Name</div>
          </template>
          <template #name="{ row }">
            <div class="table-default__left">
              <router-link
                  :to="{ name: 'island-vacation-groups-detail-clients', params: { id: row.id } }"
                  class="table-default__link-border"
              >
                {{ row.name }}
              </router-link>
            </div>
          </template>
          <template #clients="{ row }">
            <span
              v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
              :style="{cursor: 'pointer'}"
            >
              {{ row.clients.length }}
            </span>
          </template>
          <template #tour_start="{ row }">
            {{ dateFormat(row.tour_start) }}
          </template>
          <template #tour_end="{ row }">
            {{ dateFormat(row.tour_end) }}
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getIslandVacationGroupsByDates } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'IslandVacationGroupsList',
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      search: '',
      filter_from: null,
      filter_to: null,
      trips_table: [],
      table_length: 0,
      table: {
        items: [],
        columns: ['name', 'clients', 'tour_start', 'tour_end'],
        options: {
          headings: {
            'name': 'Name',
            'clients': 'Pax',
            'tour_start': 'Start',
            'tour_end': 'Finish',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      category_filter: 'upcoming',
      category_options: [
        {
          id: 'upcoming',
          name: 'Upcoming',
        },
        {
          id: 'archive',
          name: 'Archive',
        },
      ],
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  computed: {
    is_sales_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      const params = {
        page: this.pagination.page,
        page_size: 40,
        search: this.search,
        category: this.search ? '' : this.category_filter,
        group_by: 'tour_start',
        range_type: 'day',
      }
      if (!this.search && this.filter_from) params.tour_start__gte = this.filter_from
      if (!this.search && this.filter_to) params.tour_start__lte = this.filter_to
      getIslandVacationGroupsByDates(params)
        .then(res => {
          this.trips_table = res.data.results.length ? res.data.results : [{name: '', items: []}]
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.table_length = 0
          res.data.results.forEach(item => {
            this.table_length += item.items.length
          })
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onChangeCategory() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM') : ''
    },
  }
}
</script>

<style lang="sass" scoped>
.table-block
  margin-bottom: 30px
</style>
